import { stageKey, trainingRole } from '@core/shared/stages';
import type { StageProgressEntry, UserDoc } from './types';
import type { StageContent } from './useOrgData';

/**
 * The gating ladder (§13.4). Each stage unlocks the next; the stage list is
 * configurable per org, so the engine reads the order from config rather than
 * hard-coding it.
 *
 * Unlock is derived at read time — nothing writes an "unlocked" flag. That keeps
 * the client honest: a rep cannot be advanced by a stale document, and
 * `stageProgress` (which only the server writes) is the single source of truth.
 *
 * §4.1 — the simulation stages are role-scoped where the org configures them
 * that way, because a setter's practice call and a closer's are different
 * content testing different work. The rungs for a rep with `salesRole: closer`
 * are the closer's, and their progress is recorded under its own key.
 */

export type RungState = 'passed' | 'available' | 'locked' | 'expired' | 'awaiting_content';

export interface LadderConfig {
  stages: string[];
  roleScopedStages?: string[];
  /** §6.10 — tracks unlocked by finishing the ladder rather than rungs on it. */
  advancedTracks?: string[];
}

export interface Rung {
  stage: string;
  /** Where progress for this rung is recorded — `practice:closer` and the like. */
  key: string;
  index: number;
  label: string;
  blurb: string;
  state: RungState;
  /** A knowledge stage teaches before it tests: lessons first, then the check
   *  (§6.4). A drill stage goes straight to repetition. */
  kind: 'drill' | 'knowledge' | 'simulation';
  progress: StageProgressEntry | null;
  contentId: string | null;
  /** Every piece of content eligible for this rung — the variant pool (§14.3). */
  contentIds: string[];
  /**
   * §6.10 — an advanced track rather than a rung on the ladder. Unlocked by
   * passing `summative`, and never a gate to anything: it is continuing
   * development for reps who are already certified, so it sits apart from the
   * progression rather than extending it.
   */
  advanced?: boolean;
}

/** §6.5 — a rep is taught the standard before being tested on it. */
const KNOWLEDGE_STAGES = new Set(['compliance', 'language', 'financial']);
const SIMULATION_STAGES = new Set(['practice', 'summative']);

const STAGE_COPY: Record<string, { label: string; blurb: string }> = {
  persona_drill: { label: 'Read the prospect', blurb: 'Identify who you are talking to.' },
  objection_drill: { label: 'Read the objection', blurb: 'Identify what they are objecting to.' },
  compliance: { label: 'The standard', blurb: 'What you may and may not say on a call.' },
  // §6.9 — the words themselves, concrete and without theory. The theory is the
  // tactics track, and it comes after certification.
  language: { label: 'The words', blurb: 'The phrases this floor uses, and the ones it does not.' },
  financial: { label: 'Money literacy', blurb: 'Understand what a prospect tells you — without advising.' },
  practice: { label: 'Practice calls', blurb: 'Full simulations. Unlimited, and nothing is hidden.' },
  summative: { label: 'Certification', blurb: 'One blind attempt, scored by the server.' },
  // §6.10 — after certification, not before it.
  tactics: {
    label: 'Why it works',
    blurb: 'The techniques behind the calls, and where each one stops being honest.',
  },
};

/** What a role-scoped rung is actually about, so the two tracks read differently. */
const ROLE_BLURB: Record<string, Record<string, string>> = {
  setter: {
    practice: 'Full cold calls, start to transfer. Unlimited, and nothing is hidden.',
    summative: 'One blind cold call, scored by the server.',
  },
  closer: {
    practice: 'Live transfers picked up mid-call. Unlimited, and nothing is hidden.',
    summative: 'One blind transfer, scored by the server.',
  },
};

/**
 * §6.9 — compliance is the only stage that expires. A pass is spent either when
 * its interval elapses or when the org's compliance source document is bumped,
 * because the point of the stage is that a rep knows the *current* standard.
 */
export function isExpired(
  entry: StageProgressEntry | undefined,
  currentSourceVersion: string | null,
  now = new Date()
): boolean {
  if (!entry?.passed) return false;
  if (entry.expiresAt && new Date(entry.expiresAt) <= now) return true;
  if (currentSourceVersion && entry.sourceVersion && entry.sourceVersion !== currentSourceVersion) return true;
  return false;
}

/**
 * §4.1 — content for a rep in this sales role. A document tagged `both`, or
 * tagged with no role at all, serves everyone; the drills and knowledge checks
 * are authored that way. A document tagged for the other role is not theirs.
 */
function servesRole(doc: StageContent, salesRole: string | null): boolean {
  const role = (doc as { role?: string }).role;
  if (!role || role === 'both') return true;
  return role === salesRole;
}

export function buildLadder(
  config: LadderConfig,
  user: (Pick<UserDoc, 'stageProgress' | 'unitProgress'> & {
    salesRole?: string | null;
    trainingTrack?: string | null;
  }) | null,
  content: StageContent[],
  now = new Date(),
  /**
   * §13.4 — open every rung, for somebody rehearsing rather than certifying.
   *
   * The gating is the product for a rep: each stage teaches what the next one
   * assumes, and skipping is the thing it exists to prevent. It is an obstacle
   * for a manager or admin checking that the certification call is right, who
   * would otherwise have to pass six stages to read the seventh.
   *
   * Safe because it changes nothing a rep can reach and nothing the server
   * trusts. The client decides what to *offer*; `certifies()` decides what an
   * attempt *earns*, from the app role in the custom claim, and it answers no
   * for everybody but a rep. A rehearsal can open the certification call in any
   * order and still cannot be certified by it.
   */
  explore = false
): Rung[] {
  // A rep's salesRole, or the track a manager or admin chose to rehearse on.
  // Everything below is the same either way: the ladder does not know or care
  // which of the two it is looking at.
  const salesRole = trainingRole(user);
  const rungs: Rung[] = [];
  let previousCleared = true;

  config.stages.forEach((stage, index) => {
    const key = stageKey(stage, salesRole, config.roleScopedStages);
    const progress = user?.stageProgress?.[key] ?? null;

    const eligible = content.filter((doc) => doc.stage === stage && servesRole(doc, salesRole));
    // Stable order, so a rep whose content has not changed sees the same rung
    // behind the same label between renders.
    eligible.sort((a, b) => a.id.localeCompare(b.id));
    const doc = eligible[0] ?? null;

    const expired = isExpired(progress ?? undefined, doc?.sourceVersion ?? null, now);
    const cleared = !!progress?.passed && !expired;

    let state: RungState;
    // Exploring turns a locked rung available and leaves every other state
    // alone: a pass is still a pass and an expired stage still reads expired,
    // because those are facts about this account rather than about the gate.
    if (!previousCleared && !explore) state = 'locked';
    else if (expired) state = 'expired';
    else if (cleared) state = 'passed';
    else if (!doc) state = 'awaiting_content';
    else state = 'available';

    const copy = STAGE_COPY[stage] ?? { label: stage, blurb: '' };
    const blurb = (salesRole && ROLE_BLURB[salesRole]?.[stage]) ?? copy.blurb;

    rungs.push({
      stage, key, index, label: copy.label, blurb, state, progress,
      kind: KNOWLEDGE_STAGES.has(stage) ? 'knowledge'
        : SIMULATION_STAGES.has(stage) ? 'simulation' : 'drill',
      contentId: doc?.id ?? null,
      contentIds: eligible.map((d) => d.id),
    });
    previousCleared = cleared;
  });

  /**
   * §6.10 — the advanced tracks, after the ladder rather than on it.
   *
   * Unlocked by passing `summative`, which is also why they cannot be rungs: a
   * rung gates the one below it, and nothing is gated by these. A rep who never
   * opens them is still certified and still working.
   *
   * A track with no content published yet is left out entirely rather than
   * shown as locked. A permanent empty row on the home screen is the kind of
   * thing a rep learns to scroll past, and it would train them to scroll past
   * the real one when it arrives.
   */
  const certified = explore
    || rungs.some((r) => r.stage === 'summative' && r.state === 'passed');
  (config.advancedTracks ?? []).forEach((stage, i) => {
    const eligible = content
      .filter((doc) => doc.stage === stage && servesRole(doc, salesRole))
      .sort((a, b) => a.id.localeCompare(b.id));
    if (eligible.length === 0) return;

    const key = stageKey(stage, salesRole, config.roleScopedStages);
    const progress = user?.stageProgress?.[key] ?? null;
    const copy = STAGE_COPY[stage] ?? { label: stage, blurb: '' };

    /**
     * §6.12 — a track is finished when every unit in it is, not when the last
     * attempt on it passed. The units arrive over time, so a rep who cleared
     * the shelf in March is no longer finished in June, and the row should say
     * so rather than staying green over new content they have not opened.
     */
    const done = eligible.every((doc) => user?.unitProgress?.[doc.id]?.passed);

    rungs.push({
      stage, key, index: config.stages.length + i, label: copy.label, blurb: copy.blurb,
      state: !certified ? 'locked' : done ? 'passed' : 'available',
      kind: 'knowledge',
      progress,
      contentId: eligible[0]?.id ?? null,
      contentIds: eligible.map((d) => d.id),
      advanced: true,
    });
  });

  return rungs;
}

/** The rung a rep is actually standing on — the first one not yet cleared. */
export const currentRung = (rungs: Rung[]): Rung | null =>
  rungs.find((r) => r.state !== 'passed') ?? null;

/**
 * §4.1 — hybrid is an interim qualification permitting a rep to work either role
 * on a given shift. It is derived from the two entitlements and never stored.
 */
export const isHybrid = (user: UserDoc | null): boolean =>
  !!user?.entitlements?.setter?.certified && !!user?.entitlements?.closer?.certified;
